/**
 * Modern App Bar Component
 * 
 * A modern, responsive app bar with Material Design 3 principles
 */

import React, { useState } from 'react';
import {
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  Button,
  Box,
  Badge,
  Menu,
  MenuItem,
  Divider,
  Avatar,
  Tooltip,
  Chip,
  Stack,
  useTheme,
  alpha,
  InputBase,
  Paper,
} from '@mui/material';
import {
  Search as SearchIcon,
  Notifications as NotificationsIcon,
  Save as SaveIcon,
  Share as ShareIcon,
  Download as DownloadIcon,
  Undo as UndoIcon,
  Redo as RedoIcon,
  PlayArrow as PlayIcon,
  Fullscreen as FullscreenIcon,
  MoreVert as MoreVertIcon,
  Cloud as CloudIcon,
  CloudOff as CloudOffIcon,
} from '@mui/icons-material';

interface ModernAppBarProps {
  projectName?: string;
  isSaved?: boolean;
  onSave?: () => void;
  onExport?: () => void;
  onShare?: () => void;
  onUndo?: () => void;
  onRedo?: () => void;
  canUndo?: boolean;
  canRedo?: boolean;
  onSearch?: (query: string) => void;
}

const ModernAppBar: React.FC<ModernAppBarProps> = ({
  projectName = 'Untitled Project',
  isSaved = true,
  onSave,
  onExport,
  onShare,
  onUndo,
  onRedo,
  canUndo = false,
  canRedo = false,
  onSearch,
}) => {
  const theme = useTheme();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [notificationAnchor, setNotificationAnchor] = useState<null | HTMLElement>(null);
  const [searchQuery, setSearchQuery] = useState('');

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleNotificationOpen = (event: React.MouseEvent<HTMLElement>) => {
    setNotificationAnchor(event.currentTarget);
  };

  const handleNotificationClose = () => {
    setNotificationAnchor(null);
  };

  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSearch?.(searchQuery);
  };

  return (
    <AppBar
      position="fixed"
      elevation={0}
      sx={{
        zIndex: (theme) => theme.zIndex.drawer + 1,
        backgroundColor: theme.palette.background.paper,
        borderBottom: `1px solid ${theme.palette.divider}`,
        color: theme.palette.text.primary,
      }}
    >
      <Toolbar sx={{ gap: 2 }}>
        {/* Project Info Section */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexGrow: 0 }}>
          <Typography variant="h6" fontWeight="medium" noWrap>
            {projectName}
          </Typography>
          <Chip
            icon={isSaved ? <CloudIcon /> : <CloudOffIcon />}
            label={isSaved ? 'Saved' : 'Unsaved changes'}
            size="small"
            color={isSaved ? 'success' : 'warning'}
            variant="outlined"
          />
        </Box>

        {/* Action Buttons Group */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Tooltip title="Undo (Ctrl+Z)">
            <span>
              <IconButton
                onClick={onUndo}
                disabled={!canUndo}
                size="small"
                sx={{
                  color: canUndo ? 'inherit' : 'action.disabled',
                }}
              >
                <UndoIcon />
              </IconButton>
            </span>
          </Tooltip>

          <Tooltip title="Redo (Ctrl+Y)">
            <span>
              <IconButton
                onClick={onRedo}
                disabled={!canRedo}
                size="small"
                sx={{
                  color: canRedo ? 'inherit' : 'action.disabled',
                }}
              >
                <RedoIcon />
              </IconButton>
            </span>
          </Tooltip>

          <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />

          <Button
            variant="contained"
            startIcon={<SaveIcon />}
            onClick={onSave}
            size="small"
            sx={{
              textTransform: 'none',
              borderRadius: 2,
              px: 2,
            }}
          >
            Save
          </Button>

          <Button
            variant="outlined"
            startIcon={<ShareIcon />}
            onClick={onShare}
            size="small"
            sx={{
              textTransform: 'none',
              borderRadius: 2,
              px: 2,
            }}
          >
            Share
          </Button>

          <Tooltip title="Export">
            <IconButton onClick={onExport} size="small">
              <DownloadIcon />
            </IconButton>
          </Tooltip>
        </Box>

        {/* Spacer */}
        <Box sx={{ flexGrow: 1 }} />

        {/* Search Bar */}
        <Paper
          component="form"
          onSubmit={handleSearchSubmit}
          sx={{
            p: '2px 4px',
            display: 'flex',
            alignItems: 'center',
            width: 300,
            backgroundColor: alpha(theme.palette.action.hover, 0.04),
            borderRadius: 2,
            border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
            '&:hover': {
              backgroundColor: alpha(theme.palette.action.hover, 0.08),
            },
            '&:focus-within': {
              border: `1px solid ${theme.palette.primary.main}`,
              backgroundColor: theme.palette.background.paper,
            },
          }}
          elevation={0}
        >
          <IconButton sx={{ p: '10px' }} aria-label="search">
            <SearchIcon />
          </IconButton>
          <InputBase
            sx={{ ml: 1, flex: 1 }}
            placeholder="Search components..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </Paper>

        {/* Right Side Actions */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Tooltip title="Run Simulation">
            <IconButton color="primary" size="small">
              <PlayIcon />
            </IconButton>
          </Tooltip>

          <Tooltip title="Fullscreen">
            <IconButton size="small">
              <FullscreenIcon />
            </IconButton>
          </Tooltip>

          <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />

          {/* Notifications */}
          <Tooltip title="Notifications">
            <IconButton onClick={handleNotificationOpen} size="small">
              <Badge badgeContent={3} color="error">
                <NotificationsIcon />
              </Badge>
            </IconButton>
          </Tooltip>

          {/* More Options */}
          <Tooltip title="More options">
            <IconButton onClick={handleMenuOpen} size="small">
              <MoreVertIcon />
            </IconButton>
          </Tooltip>
        </Box>

        {/* Notifications Menu */}
        <Menu
          anchorEl={notificationAnchor}
          open={Boolean(notificationAnchor)}
          onClose={handleNotificationClose}
          PaperProps={{
            sx: {
              width: 360,
              maxHeight: 400,
            },
          }}
        >
          <Box sx={{ p: 2 }}>
            <Typography variant="h6">Notifications</Typography>
          </Box>
          <Divider />
          <MenuItem onClick={handleNotificationClose}>
            <Box>
              <Typography variant="subtitle2">Design validation complete</Typography>
              <Typography variant="caption" color="text.secondary">
                2 warnings found in speaker coverage
              </Typography>
            </Box>
          </MenuItem>
          <MenuItem onClick={handleNotificationClose}>
            <Box>
              <Typography variant="subtitle2">New device update available</Typography>
              <Typography variant="caption" color="text.secondary">
                Parlé TCM-X updated to v2.1
              </Typography>
            </Box>
          </MenuItem>
          <MenuItem onClick={handleNotificationClose}>
            <Box>
              <Typography variant="subtitle2">Export completed</Typography>
              <Typography variant="caption" color="text.secondary">
                PDF exported successfully
              </Typography>
            </Box>
          </MenuItem>
        </Menu>

        {/* Options Menu */}
        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleMenuClose}
        >
          <MenuItem onClick={handleMenuClose}>Project Settings</MenuItem>
          <MenuItem onClick={handleMenuClose}>Export as PDF</MenuItem>
          <MenuItem onClick={handleMenuClose}>Export as DWG</MenuItem>
          <MenuItem onClick={handleMenuClose}>Export as JSON</MenuItem>
          <Divider />
          <MenuItem onClick={handleMenuClose}>View History</MenuItem>
          <MenuItem onClick={handleMenuClose}>Keyboard Shortcuts</MenuItem>
          <MenuItem onClick={handleMenuClose}>Help & Documentation</MenuItem>
        </Menu>
      </Toolbar>
    </AppBar>
  );
};

export default ModernAppBar;